/**
 * Cart Subscription Widget
 * Handles switching between one-time purchase and subscription in cart
 * Single dropdown approach like Fable & Mane
 */

class CartSubscriptionWidget {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('change', (e) => {
      // Handle subscription dropdown changes
      if (e.target.matches('[data-cart-subscription-select]')) {
        this.handleSubscriptionChange(e.target);
      }
    });
  }

  handleSubscriptionChange(select) {
    const lineKey = select.dataset.lineKey;
    const sellingPlanId = select.value;
    
    // If empty value, it's one-time purchase (no selling plan)
    // If has value, it's a subscription with that selling plan ID
    this.updateCartLineItem(lineKey, sellingPlanId || null);
  }

  async updateCartLineItem(lineKey, sellingPlanId) {
    try {
      // Show loading state on widget and cart drawer
      this.setLoadingState(lineKey, true);
      this.showCartLoading(true);

      // Get current cart to find the line item details
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Find the current line item
      const currentItem = cart.items.find(item => item.key === lineKey);
      
      if (!currentItem) {
        throw new Error('Item not found in cart');
      }

      // Prepare the new item data (with selling plan if subscription)
      const newItemData = {
        items: [{
          id: currentItem.variant_id,
          quantity: currentItem.quantity,
          properties: {}
        }]
      };

      // Preserve existing properties (excluding internal Shopify/Recharge properties)
      if (currentItem.properties) {
        for (const [key, value] of Object.entries(currentItem.properties)) {
          // Skip internal properties that shouldn't be copied
          if (!key.startsWith('_') && key !== '__cart') {
            newItemData.items[0].properties[key] = value;
          }
        }
      }

      // Add selling_plan if it's a subscription (must be integer)
      if (sellingPlanId) {
        newItemData.items[0].selling_plan = parseInt(sellingPlanId, 10);
      }

      console.log('Adding new item with data:', newItemData);

      // Step 1: Add the new item with the updated selling plan
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(newItemData)
      });

      if (!addResponse.ok) {
        const errorData = await addResponse.json();
        console.error('Add error:', errorData);
        throw new Error(errorData.description || 'Failed to add item with new subscription');
      }

      const addData = await addResponse.json();
      console.log('Item added successfully:', addData);

      // Step 2: Remove the old item
      const removeResponse = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0
        })
      });

      if (!removeResponse.ok) {
        throw new Error('Failed to remove old item');
      }

      console.log('Old item removed successfully');

      // Refresh the cart
      await this.refreshCart();

      // Remove loading state
      this.setLoadingState(lineKey, false);
      this.showCartLoading(false);

      // Trigger theme cart refresh event if available
      if (typeof document !== 'undefined') {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { 
          bubbles: true 
        }));
      }

      console.log('Subscription updated successfully');

    } catch (error) {
      console.error('Error updating cart:', error);
      this.setLoadingState(lineKey, false);
      this.showCartLoading(false);
      
      // Show error message to user
      this.showError(error.message || 'Failed to update subscription. Please try again.');
    }
  }

  async refreshCart() {
    try {
      // Fetch the updated cart HTML
      const response = await fetch('?section_id=api-cart-items');
      
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      const html = await response.text();
      
      // Parse the response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Update cart items
      const newCartItems = doc.querySelector('[data-api-line-items]');
      const currentCartItems = document.querySelector('[data-api-line-items]');
      
      if (newCartItems && currentCartItems) {
        currentCartItems.innerHTML = newCartItems.innerHTML;
      }

      // Update cart price
      const newCartPrice = doc.querySelector('[data-api-cart-price]');
      const currentCartPrice = document.querySelector('[data-api-cart-price]');
      
      if (newCartPrice && currentCartPrice) {
        currentCartPrice.innerHTML = newCartPrice.innerHTML;
      }

      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'));

    } catch (error) {
      console.error('Error refreshing cart:', error);
      // Fallback: reload the page
      window.location.reload();
    }
  }

  setLoadingState(lineKey, isLoading) {
    const widget = document.querySelector(`[data-cart-subscription-widget][data-line-key="${lineKey}"]`);
    
    if (widget) {
      if (isLoading) {
        widget.classList.add('is-loading');
        widget.style.opacity = '0.5';
        widget.style.pointerEvents = 'none';
      } else {
        widget.classList.remove('is-loading');
        widget.style.opacity = '1';
        widget.style.pointerEvents = 'auto';
      }
    }
  }

  showCartLoading(show) {
    // Find or create the loading overlay
    let overlay = document.getElementById('cart-subscription-loading');
    
    if (show) {
      if (!overlay) {
        // Create the loading overlay
        overlay = document.createElement('div');
        overlay.id = 'cart-subscription-loading';
        overlay.className = 'cart-subscription-loading-overlay';
        overlay.innerHTML = `
          <div class="cart-subscription-loading-content">
            <div class="cart-subscription-spinner"></div>
            <p class="cart-subscription-loading-text">Updating subscription...</p>
          </div>
        `;
        
        // Append to cart drawer
        const cartDrawer = document.querySelector('[data-cart-drawer-inner]') || 
                          document.querySelector('.drawer__inner') || 
                          document.querySelector('cart-drawer') ||
                          document.body;
        cartDrawer.appendChild(overlay);
      }
      
      // Show overlay
      overlay.style.display = 'flex';
      
      // Disable cart interactions
      const cartDrawer = document.querySelector('cart-drawer') || 
                        document.querySelector('.drawer--cart');
      if (cartDrawer) {
        cartDrawer.style.pointerEvents = 'none';
      }
      
    } else {
      // Hide overlay
      if (overlay) {
        overlay.style.display = 'none';
      }
      
      // Re-enable cart interactions
      const cartDrawer = document.querySelector('cart-drawer') || 
                        document.querySelector('.drawer--cart');
      if (cartDrawer) {
        cartDrawer.style.pointerEvents = 'auto';
      }
    }
  }

  showError(message) {
    // You can customize this to match your theme's notification system
    alert(message);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartSubscriptionWidget();
  });
} else {
  new CartSubscriptionWidget();
}

